import loadScript from 'lib/window/loadScript'

// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
interface TurnstileRenderOptions {
  sitekey: string
  action: string
  appearance: 'interaction-only'
  theme: 'light'
  size: 'flexible'
  retry: 'never'
  callback: (token: string) => void
  'before-interactive-callback': () => void
  // Returning true tells Turnstile the error was handled.
  'error-callback': (errorCode: string) => boolean
  'unsupported-callback': () => void
}

export interface Turnstile {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined
  getResponse: (widgetId: string) => string | undefined
  isExpired: (widgetId: string) => boolean
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: Turnstile
  }
}

// Explicit rendering stops api.js from rendering widgets on load, so a widget only runs when the login form renders one.
// Cloudflare requires this exact URL: a proxied or cached copy breaks when they update it.
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile)

  return loadScript(TURNSTILE_SRC, { async: true }, { shareInFlight: true, removeOnError: true }).then(
    () => {
      if (window.turnstile) return window.turnstile
      // loadScript would count this tag as loaded, so remove it for the next call to download again.
      document.querySelector(`script[src="${TURNSTILE_SRC}"]`)?.remove()
      throw new Error('Turnstile did not initialise')
    },
    () => {
      throw new Error('Turnstile failed to load')
    },
  )
}
